import { useQuery, useQueries, type UseQueryResult } from '@tanstack/react-query'
import { type PaginatedResponse, type DetailResponse } from '../../../api/client'
import { type PaginationOptions } from '../../../api/pagination'
import {
  fetchArticle,
  fetchArticles,
  fetchAllArticles,
  fetchSharepoint,
  fetchSharepoints,
  fetchFeedbackForm,
  fetchFeedbackForms,
  fetchFeedbackSubmissions,
  fetchCategories,
  fetchCategory,
  fetchAllCategories,
  fetchProperties,
  fetchProperty,
  fetchAllProperties,
  fetchTypes,
  fetchType,
  fetchAllTypes,
  fetchFamilies,
  fetchFamily,
  fetchAllFamilies,
  fetchMaterials,
  fetchAllMaterials,
  fetchBrands,
  fetchBrandOptions,
  searchArticles,
  type ArticleFilters,
} from '../api/articleApi'
import { type Article, type Sharepoint, type FeedbackForm, type FeedbackSubmission, type Property, type Type, type Family, type Material } from '../utils/types'
import { type Category } from '../../Supplier/utils/types'
import { type PaginatedQueryParameters, type DetailQueryParameters } from '../../../api/hooks/utils/types'
import { useSmartListQuery, useSmartDetailQuery } from '../../../api/hooks/useSmartQueries'
import { articleKeys, sharepointKeys, categoryKeys, propertyKeys, typeKeys, familyKeys } from '../utils/queryKeys'
import { getPKfromSelf } from '../../../utils/functions'

/**
 * Hook that provides the available Article instances that are matching the
 * specified query parameters.
 * @param parameters - The parameters that define which instances to fetch from the backend.
 * @param instances - Array of instance ids or urls to fetch from the backend.
 * @param enabled - Disabling the query will prevent it from running instantly
 *  after the parameters or instances changed.
 */
export function useArticles(
  parameters: PaginatedQueryParameters<Article, ArticleFilters>,
  instances: number[] | string[] | undefined = undefined,
  enabled = true,
): UseQueryResult<PaginatedResponse<Article>> {
  return useSmartListQuery<Article, ArticleFilters>(
    {
      queryKeyFactory: articleKeys,
      queryParameters: parameters,
      queryFn: (_, ids: number[] | undefined) => fetchArticles(parameters, ids),
      elasticSearchFn: () => searchArticles(parameters)
    },
    enabled,
    instances,
  )
}

/**
 * Hook to fetch all articles (all pages)
 */
export function useAllArticles(
  options: Omit<PaginationOptions, 'page'> = {},
  enabled = true
): UseQueryResult<Article[], Error> {
  return useQuery({
    queryKey: [...articleKeys.all, 'all', options],
    queryFn: () => fetchAllArticles(options),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch a single article by ID or URL
 */
export function useArticle(
  idOrUrl: number | string,
  parameters: DetailQueryParameters<Article>,
  enabled = true,
): UseQueryResult<DetailResponse<Article>> {
  return useSmartDetailQuery(
    idOrUrl,
    {
      queryKeyFactory: articleKeys,
      queryParameters: parameters,
      queryFn: (_, id) => fetchArticle(id, parameters.expand),
    },
    enabled
  )
}

/**
 * Hook to fetch paginated sharepoints
 */
export function useSharepoints(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Sharepoint>, Error> {
  return useQuery({
    queryKey: sharepointKeys.list({
      page: options.page,
      pageSize: options.pageSize,
      filters: options.filters,
    }),
    queryFn: () => fetchSharepoints(options),
    enabled,
  })
}

/**
 * Hook to fetch a single sharepoint by ID or URL
 */
export function useSharepoint(
  idOrUrl: string | number | undefined,
  expand?: string[],
  enabled = true
): UseQueryResult<Sharepoint, Error> {
  return useQuery({
    queryKey: sharepointKeys.detail(typeof idOrUrl === 'number' ? idOrUrl : Number(getPKfromSelf(String(idOrUrl)))),
    queryFn: () => {
      if (!idOrUrl) {
        throw new Error('Sharepoint ID or URL is required')
      }
      return fetchSharepoint(idOrUrl, expand)
    },
    enabled: enabled && idOrUrl !== undefined,
  })
}

/**
 * Hook to fetch all feedback forms
 */
export function useFeedbackForms(
  enabled = true
): UseQueryResult<FeedbackForm[], Error> {
  return useQuery({
    queryKey: ['feedbackForms'],
    queryFn: fetchFeedbackForms,
    enabled,
  })
}

/**
 * Hook to fetch feedback form
 */
export function useFeedbackForm(
  id: string | number | undefined,
  enabled = true
): UseQueryResult<FeedbackForm, Error> {
  return useQuery({
    queryKey: ['feedbackForms', String(id)],
    queryFn: () => {
      if (!id) {
        throw new Error('Feedback form ID is required')
      }
      return fetchFeedbackForm(id)
    },
    enabled: enabled && id !== undefined,
  })
}

/**
 * Hook to fetch feedback submissions for a sharepoint
 */
export function useFeedbackSubmissions(
  sharepointId: string | number | undefined,
  enabled = true
): UseQueryResult<FeedbackSubmission[], Error> {
  return useQuery({
    queryKey: ['feedbackSubmissions', String(sharepointId)],
    queryFn: () => {
      if (!sharepointId) {
        throw new Error('Sharepoint ID is required')
      }
      return fetchFeedbackSubmissions(sharepointId)
    },
    enabled: enabled && sharepointId !== undefined,
  })
}

/**
 * Hook to fetch paginated categories
 */
export function useCategories(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Category>, Error> {
  return useQuery({
    queryKey: categoryKeys.list({
      page: options.page,
      pageSize: options.pageSize,
      filters: options.filters,
    }),
    queryFn: () => fetchCategories(options),
    enabled,
  })
}

/**
 * Hook to fetch a single category by ID
 */
export function useCategory(
  id: string | number | undefined,
  enabled = true
): UseQueryResult<Category, Error> {
  return useQuery({
    queryKey: categoryKeys.detail(typeof id === 'number' ? id : Number(id)),
    queryFn: () => {
      if (!id) {
        throw new Error('Category ID is required')
      }
      return fetchCategory(id)
    },
    enabled: enabled && id !== undefined,
  })
}

/**
 * Hook to fetch all categories (all pages)
 */
export function useAllCategories(
  options: Omit<PaginationOptions, 'page'> = {},
  enabled = true
): UseQueryResult<Category[], Error> {
  return useQuery({
    queryKey: [...categoryKeys.all, 'all', options],
    queryFn: () => fetchAllCategories(options),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - categories don't change often
  })
}

/**
 * Hook to fetch paginated properties
 */
export function useProperties(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Property>, Error> {
  return useQuery({
    queryKey: propertyKeys.list({
      page: options.page,
      pageSize: options.pageSize,
      filters: options.filters,
    }),
    queryFn: () => fetchProperties(options),
    enabled,
  })
}

/**
 * Hook to fetch a single property by ID
 */
export function useProperty(
  id: string | number | undefined,
  enabled = true
): UseQueryResult<Property, Error> {
  return useQuery({
    queryKey: propertyKeys.detail(typeof id === 'number' ? id : Number(id)),
    queryFn: () => {
      if (!id) {
        throw new Error('Property ID is required')
      }
      return fetchProperty(id)
    },
    enabled: enabled && id !== undefined,
  })
}

/**
 * Hook to fetch all properties (all pages)
 */
export function useAllProperties(
  options: Omit<PaginationOptions, 'page'> = {},
  enabled = true
): UseQueryResult<Property[], Error> {
  return useQuery({
    queryKey: [...propertyKeys.all, 'all', options],
    queryFn: () => fetchAllProperties(options),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - properties don't change often
  })
}

/**
 * Hook to fetch paginated types
 */
export function useTypes(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Type>, Error> {
  return useQuery({
    queryKey: typeKeys.list({
      page: options.page,
      pageSize: options.pageSize,
      filters: options.filters,
    }),
    queryFn: () => fetchTypes(options),
    enabled,
  })
}

/**
 * Hook to fetch a single type by ID
 */
export function useType(
  id: string | number | undefined,
  enabled = true
): UseQueryResult<Type, Error> {
  return useQuery({
    queryKey: typeKeys.detail(typeof id === 'number' ? id : Number(id)),
    queryFn: () => {
      if (!id) {
        throw new Error('Type ID is required')
      }
      return fetchType(id)
    },
    enabled: enabled && id !== undefined,
  })
}

/**
 * Hook to fetch all types (all pages)
 */
export function useAllTypes(
  options: Omit<PaginationOptions, 'page'> = {},
  enabled = true
): UseQueryResult<Type[], Error> {
  return useQuery({
    queryKey: [...typeKeys.all, 'all', options],
    queryFn: () => fetchAllTypes(options),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - types don't change often
  })
}

/**
 * Hook to fetch paginated families
 */
export function useFamilies(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Family>, Error> {
  return useQuery({
    queryKey: familyKeys.list({
      page: options.page,
      pageSize: options.pageSize,
      filters: options.filters,
    }),
    queryFn: () => fetchFamilies(options),
    enabled,
  })
}

/**
 * Hook to fetch a single family by ID
 */
export function useFamily(
  id: string | number | undefined,
  enabled = true
): UseQueryResult<Family, Error> {
  return useQuery({
    queryKey: familyKeys.detail(typeof id === 'number' ? id : Number(id)),
    queryFn: () => {
      if (!id) {
        throw new Error('Family ID is required')
      }
      return fetchFamily(id)
    },
    enabled: enabled && id !== undefined,
  })
}

/**
 * Hook to fetch all families (all pages)
 */
export function useAllFamilies(
  options: Omit<PaginationOptions, 'page'> = {},
  enabled = true
): UseQueryResult<Family[], Error> {
  return useQuery({
    queryKey: [...familyKeys.all, 'all', options],
    queryFn: () => fetchAllFamilies(options),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - families don't change often
  })
}

/**
 * Hook to fetch multiple articles from Elasticsearch search results
 * Loads full article data from the database for each article ID
 * 
 * @param articleIds - Array of article IDs from Elasticsearch search
 * @param expand - Fields to expand in the article response
 * @param enabled - Whether to enable the queries
 * @returns Object with articles, loading states, and errors
 * 
 * @example
 * ```tsx
 * const { articles, isLoading, hasErrors } = useArticlesFromSearch(
 *   ['1', '2', '3'],
 *   ['files', 'category', 'family', 'properties', 'supplier', 'requests']
 * )
 * ```
 */
export function useArticlesFromSearch(
  articleIds: string[],
  expand?: string[],
  enabled = true
) {
  const queries = useQueries({
    queries: articleIds.map((id) => ({
      queryKey: articleKeys.detail(id, { expand }),
      queryFn: () => fetchArticle(id, expand),
      enabled: enabled && !!id,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  })

  // Extract articles from query results
  const articles = queries
    .filter((query) => query.isSuccess && query.data)
    .map((query) => query.data as Article)

  // Check if any queries are still loading
  const isLoading = queries.some((query) => query.isLoading)

  // Check if all queries are done (either success or error)
  const isIdle = queries.every((query) => query.status === 'pending')
  const isDone = queries.every((query) => query.status !== 'pending')

  // Check for errors
  const hasErrors = queries.some((query) => query.isError)
  const errors = queries
    .filter((query) => query.isError)
    .map((query) => query.error)

  // Calculate progress
  const totalQueries = articleIds.length
  const completedQueries = queries.filter(
    (query) => query.status !== 'pending'
  ).length
  const progress = totalQueries > 0 ? (completedQueries / totalQueries) * 100 : 0

  return {
    articles,
    isLoading,
    isIdle,
    isDone,
    hasErrors,
    errors,
    progress,
    queries, // Expose individual queries for advanced use cases
  }
}

/**
 * Hook to fetch all available brands
 */
export function useBrands(
  enabled = true
): UseQueryResult<string[], Error> {
  return useQuery({
    queryKey: ['brands'],
    queryFn: fetchBrands,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - brands don't change often
  })
}

/**
 * Hook to fetch all available brands as options for select inputs
 * Returns options in the format: { label: string }
 * Note: Brand options only have labels, no values
 */
export function useBrandOptions(
  enabled = true
): UseQueryResult<{ value?: string; label?: string; disabled?: boolean }[], Error> {
  return useQuery({
    queryKey: ['brandOptions'],
    queryFn: fetchBrandOptions,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - brands don't change often
  })
}

/**
 * Hook to fetch paginated materials
 */
export function useMaterials(
  options: PaginationOptions = {},
  enabled = true
): UseQueryResult<PaginatedResponse<Material>, Error> {
  return useQuery({
    queryKey: ['materials', options],
    queryFn: () => fetchMaterials(options),
    enabled,
  })
}

/**
 * Hook to fetch all materials (all pages)
 */
export function useAllMaterials(
  options: Omit<PaginationOptions, 'page'> = {},
  enabled = true
): UseQueryResult<Material[], Error> {
  return useQuery({
    queryKey: ['materials', 'all', options],
    queryFn: () => fetchAllMaterials(options),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - materials don't change often
  })
}
